/**
 * V3.4 (2026-06-03 P2-B helper consolidation) — single source for ISO
 * date formatting across the web app. Previously 5+ components rolled
 * their own near-duplicates (vault-panel: formatDate + formatAuthoredAt,
 * StudioListView: formatRelative, concepts-browser: formatLatestStance,
 * stance-gap-starters + stances-browser: formatAuthoredAt, concept-detail
 * page: formatAuthoredAt). All shared the same concepts; minor drift in
 * fallback strings + which date precision to render.
 *
 * Behaviour preserved:
 *   - formatDateTime: full locale string with time (vault-panel.formatDate).
 *   - formatAuthoredAt: YYYY-MM / YYYY-MM-DD / passthrough — accepts
 *     YYYY-MM regex and ISO timestamps. Returns string | null.
 *     `fallbackEmpty: '近期' | '时间不详' | null`: lets callers choose
 *     the empty-iso fallback (vault-panel + concept-detail expect null,
 *     stance-gap-starters expects '近期', stances-browser expects '时间不详').
 *   - formatRelative: 刚刚 / N 分钟前 / HH:MM 编辑 / 昨天 / N 天前 / YYYY-MM-DD.
 *     Matches StudioListView's exact buckets.
 *   - formatLatestStance: identical regex to formatAuthoredAt's "YYYY-MM"
 *     pre-check, returns '' on null (matches concepts-browser behaviour).
 *
 * No visible output changes — verify by diffing rendered surfaces.
 */

/**
 * 2026-07-13 (Leon: 时间统一 UTC+8) — every timestamp surface renders in
 * Asia/Shanghai. The product's users sit in HK/SG (UTC+8); mixing the
 * runtime's local timezone into some surfaces and UTC into others is
 * what produced both the VaultItemRow hydration mismatch and the
 * "fresh notification shows 8 小时前" bug.
 */
const APP_TZ = 'Asia/Shanghai';

/**
 * Parse a server timestamp into a Date, treating timezone-less strings
 * as UTC.
 *
 * 2026-07-13 (R103.3 Codex follow-up) — D1's `datetime('now')` emits
 * `YYYY-MM-DD HH:MM:SS`: UTC, but with no timezone marker. `new Date()`
 * / `Date.parse()` read that as LOCAL time, so a notification created
 * seconds ago rendered as «8 小时前» in an HKT browser. Anything that
 * turns a server string into a Date must come through here.
 */
export function parseServerDate(iso: string): Date {
  const trimmed = iso.trim();
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)$/.exec(trimmed);
  if (m) return new Date(`${m[1]}T${m[2]}Z`);
  return new Date(trimmed);
}

/**
 * Full date+time string, deterministic across SSR and client.
 *
 * 2026-07-13 (R103.2 Codex) — was bare `d.toLocaleString()`, which
 * formats with the runtime's own locale + timezone: CF Workers SSR
 * emitted `2026/7/12 05:38:50` while the browser hydrated
 * `7/12/2026, 5:38:50 AM` → React hydration mismatch on every
 * VaultItemRow. Pinning locale AND timeZone makes both sides render
 * the identical string.
 */
export function formatDateTime(iso: string): string {
  try {
    const d = parseServerDate(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('zh-CN', {
      timeZone: APP_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/** Date-only variant (`2026/07/12`), same UTC+8 pinning as formatDateTime. */
export function formatDate(iso: string): string {
  try {
    const d = parseServerDate(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('zh-CN', {
      timeZone: APP_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Shared 刚刚 / N 分钟前 / N 小时前 / 昨天 / N 天前 / date formatter.
 *
 * 2026-07-13 — NotificationBell, DistillReviewView, SocialImportView and
 * the share page each rolled their own near-identical copy; two of the
 * four also had the timezone-less-parse bug (see parseServerDate). One
 * implementation, one parse path.
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const t = parseServerDate(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.round((now - t) / 60_000);
  if (min < 1) return '刚刚';
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.round(hr / 24);
  if (day === 1) return '昨天';
  if (day < 14) return `${day} 天前`;
  return formatDate(iso);
}

/**
 * Render an ISO-ish date as YYYY-MM-DD / YYYY-MM / passthrough. The most
 * widely-used variant across the codebase — used wherever we surface
 * authored_at on stance/concept cards.
 *
 * `fallbackEmpty` controls what null / empty / unparseable input maps to:
 *  - null (default): same as vault-panel + concept-detail (return null).
 *  - string: stance-gap-starters used '近期', stances-browser used '时间不详'.
 */
export function formatAuthoredAt(
  iso: string | null | undefined,
  fallbackEmpty: string | null = null,
): string | null {
  if (!iso) return fallbackEmpty;
  const trimmed = iso.trim();
  if (!trimmed) return fallbackEmpty;
  // Already YYYY-MM or YYYY-MM-DD — render as-is.
  if (/^\d{4}-\d{2}(-\d{2})?$/.test(trimmed)) return trimmed;
  // ISO timestamp — render the date portion.
  const m = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1] ?? trimmed;
  return trimmed.slice(0, 10);
}

/**
 * StudioListView's relative formatter. 刚刚 / N 分钟前 / HH:MM 编辑 /
 * 昨天 / N 天前 / YYYY-MM-DD. Returns '' on invalid date so the list row
 * can keep rendering without an extra null guard.
 */
export function formatRelative(iso: string, now: number = Date.now()): string {
  const date = parseServerDate(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = (now - date.getTime()) / 1000;
  if (diff < 60) return '刚刚';
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  if (diff < 86400) {
    // HH:MM pinned to UTC+8 (was getHours() = runtime-local).
    const hhmm = date.toLocaleTimeString('zh-CN', {
      timeZone: APP_TZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${hhmm} 编辑`;
  }
  if (diff < 86400 * 2) return '昨天';
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} 天前`;
  return formatDate(iso).replaceAll('/', '-');
}

/**
 * concepts-browser's latest-stance formatter. Same shape as
 * formatAuthoredAt but returns '' (not null) on empty input — kept as
 * a thin wrapper so the caller doesn't need to swap `?? ''` everywhere.
 */
export function formatLatestStance(iso: string | null | undefined): string {
  return formatAuthoredAt(iso, '') ?? '';
}
